import { useEffect, useMemo, useState } from "react";
import { ApplicationButton, ApplicationField, ApplicationStateRegion, ApplicationSurface } from "../../../components/application";
import SamplePreview from "./SamplePreview";
import { browseAuthoringStimuli, reResolveSelectedStimuli } from "../services/activityAuthoringApi";
import { vocalTaskLabel } from "../vocalTaskPresentation";

function labelFor(row) {
  return row.presentation?.displayName || row.displayName || "Selected sample";
}

export default function SampleBrowser({ selectedIds, onChange, onResolvedSelection, methodKey = null, activityId = "new-personal" }) {
  const [search, setSearch] = useState("");
  const [vocalTask, setVocalTask] = useState("");
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [state, setState] = useState("loading");
  const [selectionState, setSelectionState] = useState(selectedIds.length ? "loading" : "ready");
  const [activePreview, setActivePreview] = useState(null);
  const [mobileSelectedOpen, setMobileSelectedOpen] = useState(false);

  async function load({ cursor = null, append = false } = {}) {
    setState("loading");
    try {
      const page = await browseAuthoringStimuli({ search, vocalTask, cursor, methodKey });
      const eligible = (page.items || []).filter(item => item.eligible && item.selectedStimulusInstanceId);
      setItems(current => append ? [...current, ...eligible] : eligible);
      setNextCursor(page.nextCursor || null);
      setState("ready");
    } catch {
      setState("error");
    }
  }

  useEffect(() => { load(); /* initial authoritative discovery */ }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let active = true;
    if (!selectedIds.length) { setSelectedRows([]); setSelectionState("ready"); return () => { active = false; }; }
    setSelectionState("loading");
    (methodKey ? reResolveSelectedStimuli(selectedIds, methodKey) : reResolveSelectedStimuli(selectedIds)).then(rows => {
      if (!active) return;
      // The service returns one row per requested identity in request order.
      setSelectedRows(rows); onResolvedSelection?.(rows); setSelectionState("ready");
    }).catch(() => active && setSelectionState("error"));
    return () => { active = false; };
  }, [selectedIds, methodKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const tasks = useMemo(() => [...new Set(items.flatMap(item => item.vocalTasks || item.presentation?.vocalTasks || []))].sort(), [items]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  function select(item) {
    const id = item.selectedStimulusInstanceId;
    if (selectedSet.has(id)) return;
    onChange([...selectedIds, id]);
  }
  function remove(id, index) { onChange(selectedIds.filter((_, candidate) => candidate !== index)); }

  return <ApplicationSurface className="av-authoring-browser" variant="subtle">
    <form className="av-authoring-browser__filters" onSubmit={event => { event.preventDefault(); load(); }}>
      <ApplicationField label="Search samples" value={search} onChange={event => setSearch(event.target.value)} />
      <label><span>Vocal task</span><select aria-label="Vocal task" value={vocalTask} onChange={event => setVocalTask(event.target.value)}><option value="">All supported tasks</option>{tasks.map(task => <option value={task} key={task}>{vocalTaskLabel(task)}</option>)}</select></label>
      <ApplicationButton type="submit">Search</ApplicationButton>
    </form>
    <ApplicationButton className="av-authoring-selected-toggle" variant="secondary" onClick={() => setMobileSelectedOpen(value => !value)}>Selected samples · {selectedIds.length}</ApplicationButton>
    <div className="av-authoring-browser__layout">
      <div className="av-authoring-browser__results" aria-label="Available samples">
        {state === "loading" ? <ApplicationStateRegion status="loading" message="Loading available samples…" /> : null}
        {state === "error" ? <><ApplicationStateRegion status="error" message="Samples could not be loaded." /><ApplicationButton variant="secondary" onClick={() => load()}>Try again</ApplicationButton></> : null}
        {state === "ready" && !items.length ? <ApplicationStateRegion status="empty" message="No eligible samples match this search." /> : null}
        {state === "ready" ? items.map(item => {
          const id = item.selectedStimulusInstanceId;
          const selected = selectedSet.has(id);
          return <article className={`av-authoring-sample ${selected ? "av-authoring-sample--selected" : ""}`} key={id}>
            <div><strong>{labelFor(item)}</strong>{(item.vocalTasks || []).length ? <small>{item.vocalTasks.map(vocalTaskLabel).join(" · ")}</small> : null}</div>
            <SamplePreview presentation={item.presentation || item} stimulusInstanceId={id} activityId={activityId} label={labelFor(item)} previewId={`available:${id}`} activePreview={activePreview} onPreviewPlay={setActivePreview} />
            <ApplicationButton variant={selected ? "quiet" : "secondary"} disabled={selected} onClick={() => select(item)}>{selected ? "Selected" : "Select"}</ApplicationButton>
          </article>;
        }) : null}
        {state === "ready" && nextCursor ? <ApplicationButton variant="secondary" onClick={() => load({ cursor: nextCursor, append: true })}>Load more</ApplicationButton> : null}
      </div>
      <aside className={`av-authoring-selected ${mobileSelectedOpen ? "av-authoring-selected--open" : ""}`} aria-label={`Selected samples · ${selectedIds.length}`}>
        <h3>Selected samples · {selectedIds.length}</h3>
        {selectionState === "loading" ? <ApplicationStateRegion status="loading" message="Restoring selected samples…" /> : null}
        {selectionState === "error" ? <ApplicationStateRegion status="error" message="Selected samples could not be restored. Try reloading the draft." /> : null}
        {selectionState === "ready" && !selectedRows.length ? <p>No samples selected yet.</p> : null}
        {selectedRows.map((row, index) => <article className="av-authoring-selected__item" key={`${row.stimulusInstanceId}:${index}`}>
          <div><strong>{labelFor(row)}</strong>{row.status !== "currently_eligible" ? <p className="av-authoring-sample__reason">This sample is no longer available for a ready activity.</p> : null}</div>
          <SamplePreview presentation={row.presentation} stimulusInstanceId={row.stimulusInstanceId} activityId={activityId} label={labelFor(row)} previewId={`selected:${row.stimulusInstanceId}:${index}`} activePreview={activePreview} onPreviewPlay={setActivePreview} />
          <div><ApplicationButton variant="quiet" onClick={() => remove(row.stimulusInstanceId, index)}>Remove</ApplicationButton></div>
        </article>)}
      </aside>
    </div>
  </ApplicationSurface>;
}
